import React, { useMemo, useState } from 'react';
import './GantView.scss';
import GanttChart from '../../../../../components/organismsv2/GanttChart/GanttChart';
import Status from '../../../../../components/Atomsv2/Status/Status';
import EditarTarea from '../formularios/EditarTarea';

// Mapeos específicos de este dominio (proyectos)
const SEVERITY  = { 1: 'default', 2: 'warning', 3: 'primary', 4: 'neutral', 5: 'success', 6: 'error' };
const COLOR_BAR = { 1:'var(--gray-800)',2:'var(--yellow-600)',3:'var(--blue-600)',4:'var(--purple-600)',5:'var(--green-600)',6:'var(--red-600)' };

const calcProgress = (tarea) => {
    const terminada = tarea.ESTATUS_ID === 5;
    let subs = [];
    try { subs = JSON.parse(tarea.SUBTAREAS || '[]'); } catch { subs = []; }
    if (subs.length === 0) return terminada ? 100 : 0;
    const done = subs.filter(s => s.ESTATUS_ID === 5).length + (terminada ? 1 : 0);
    return Math.round((done / (subs.length + 1)) * 100);
};

// Genera las filas de subtareas (indent 3) de una tarea, si tiene fechas propias
const buildSubtareaRows = (tarea) => {
    let subs = [];
    try { subs = JSON.parse(tarea.SUBTAREAS || '[]'); } catch { subs = []; }
    return subs
        .filter(s => s.FECHA_INI && s.FECHA_FIN)
        .map(sub => ({
            id:      `st-${sub.ID}`,
            label:   sub.TAREA,
            start:   sub.FECHA_INI,
            end:     sub.FECHA_FIN,
            color:   COLOR_BAR[sub.ESTATUS_ID] || '#94a3b8',
            badge:   (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Status severity={SEVERITY[sub.ESTATUS_ID] || 'default'}>{sub.ESTATUS}</Status>
                    {sub.USUARIO_ASIGNADO_FOTO && (
                        <img src={sub.USUARIO_ASIGNADO_FOTO} alt="" title={sub.USUARIO_ASIGNADO_NOMBRE?.trim() || ''} style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    )}
                </div>
            ),
            icon:    'subdirectory_arrow_right',
            indent:  3,
            progress: sub.ESTATUS_ID === 5 ? 100 : 0,
            tooltip: `${sub.TAREA} · ${sub.ESTATUS}${sub.USUARIO_ASIGNADO_NOMBRE ? ' · ' + sub.USUARIO_ASIGNADO_NOMBRE.trim() : ''} · ${sub.FECHA_INI?.split('T')[0]} → ${sub.FECHA_FIN?.split('T')[0]}`,
        }));
};

// Convierte la data de proyectos al formato genérico que espera GanttChart
const buildRows = (tareas, proyectos, areas) => {
    const rows = [];

    for (const area of areas) {
        const proyDeArea   = proyectos.filter(p => p.ID_AREA === area.ID);
        const tareasDeArea = tareas.filter(t => proyDeArea.some(p => p.ID === t.PROYECTO_ID));
        if (tareasDeArea.length === 0) continue;

        rows.push({ id: `a-${area.ID}`, label: area.AREA, icon: 'folder', isGroup: true, indent: 0 });

        for (const proy of proyDeArea) {
            const tareasDeProyecto = tareas.filter(t => t.PROYECTO_ID === proy.ID);
            if (tareasDeProyecto.length === 0) continue;

            rows.push({ id: `p-${proy.ID}`, label: proy.PROYECTO, icon: 'article', isGroup: true, indent: 1 });

            for (const tarea of tareasDeProyecto) {
                rows.push({
                    id:      `t-${tarea.ID}`,
                    label:   tarea.TAREA,
                    start:   tarea.FECHA_INI,
                    end:     tarea.FECHA_FIN,
                    color:   COLOR_BAR[tarea.ESTATUS_ID] || '#94a3b8',
                    badge:   (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Status severity={SEVERITY[tarea.ESTATUS_ID] || 'default'}>{tarea.ESTATUS}</Status>
                            {tarea.USUARIO_ASIGNADO_FOTO && (
                                <img src={tarea.USUARIO_ASIGNADO_FOTO} alt="" title={tarea.USUARIO_ASIGNADO_NOMBRE?.trim() || ''} style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            )}
                        </div>
                    ),
                    icon:    'task_alt',
                    indent:  2,
                    progress: calcProgress(tarea),
                    tooltip: `${tarea.TAREA} · ${tarea.ESTATUS}${tarea.USUARIO_ASIGNADO_NOMBRE ? ' · ' + tarea.USUARIO_ASIGNADO_NOMBRE.trim() : ''} · ${tarea.FECHA_INI?.split('T')[0]} → ${tarea.FECHA_FIN?.split('T')[0]}`,
                });
                rows.push(...buildSubtareaRows(tarea));
            }
        }
    }

    // Tareas sin proyecto/área registrado van al final sin agrupación
    const huerfanas = tareas.filter(t => !proyectos.find(p => p.ID === t.PROYECTO_ID));
    for (const tarea of huerfanas) {
        rows.push({
            id:      `t-${tarea.ID}`,
            label:   tarea.TAREA,
            start:   tarea.FECHA_INI,
            end:     tarea.FECHA_FIN,
            color:   COLOR_BAR[tarea.ESTATUS_ID] || '#94a3b8',
            badge:   (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Status severity={SEVERITY[tarea.ESTATUS_ID] || 'default'}>{tarea.ESTATUS}</Status>
                    {tarea.USUARIO_ASIGNADO_FOTO && (
                        <img src={tarea.USUARIO_ASIGNADO_FOTO} alt="" title={tarea.USUARIO_ASIGNADO_NOMBRE?.trim() || ''} style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    )}
                </div>
            ),
            icon:    'task_alt',
            indent:  0,
            progress: calcProgress(tarea),
            tooltip: `${tarea.TAREA} · ${tarea.ESTATUS}${tarea.USUARIO_ASIGNADO_NOMBRE ? ' · ' + tarea.USUARIO_ASIGNADO_NOMBRE.trim() : ''} · ${tarea.FECHA_INI?.split('T')[0]} → ${tarea.FECHA_FIN?.split('T')[0]}`,
        });
        rows.push(...buildSubtareaRows(tarea));
    }

    return rows;
};

const GantView = ({ data, setData }) => {
    const [tareaEdit, setTareaEdit] = useState(null);

    const tareas    = useMemo(() => (data?.tareas    || []).filter(t => t.FECHA_INI && t.FECHA_FIN), [data?.tareas]);
    const proyectos = data?.proyecto || [];
    const areas     = data?.area     || [];

    const rows = useMemo(() => buildRows(tareas, proyectos, areas), [tareas, proyectos, areas]);

    // row.id tiene el formato 't-{ID}' (tarea) o 'st-{ID}' (subtarea); recupera el objeto completo de data.tareas
    const handleRowClick = (row) => {
        if (row.id.startsWith('st-')) {
            const id = Number(row.id.replace('st-', ''));
            for (const t of data?.tareas || []) {
                let subs = [];
                try { subs = JSON.parse(t.SUBTAREAS || '[]'); } catch { subs = []; }
                const sub = subs.find(s => s.ID === id);
                if (sub) { setTareaEdit(sub); return; }
            }
            return;
        }
        const id    = Number(row.id.replace('t-', ''));
        const tarea = data?.tareas?.find(t => t.ID === id);
        if (tarea) setTareaEdit(tarea);
    };

    return (
        <div className="GantView">
            <GanttChart rows={rows} onClick={handleRowClick} />
            <EditarTarea
                open={!!tareaEdit}
                onClose={() => setTareaEdit(null)}
                tarea={tareaEdit}
                data={data}
                setData={setData}
            />
        </div>
    );
};

export default GantView;
